import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { ImageIcon, Plus, Trash2 } from "lucide-react";
import { ImageSelectDialog } from "@/components/ImageSelectDialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DEFAULT_FAN_CAPTURE_LABEL,
  LINK_IN_BIO_SOCIAL_PLATFORMS,
} from "@/lib/linkInBioPlatforms";
import {
  LINK_IN_BIO_FORM_FIELD_OPTIONS,
  fieldSelectionsFromFields,
  fieldsFromSelections,
  type LinkInBioFormField,
  type LinkInBioFormFieldSelections,
} from "@/types/LinkInBioFormFields";
import type { LinkInBioTemplateContext } from "./types";

type SocialRow = {
  platform: string;
  url: string;
};

type LinkRow = {
  label: string;
  url: string;
  thumbnailUrl: string;
};

export type LinkInBioTemplatePayload = {
  profilePicUrl?: string;
  socials: Array<{ platform: string; url: string }>;
  links: Array<{ label: string; url: string; thumbnailUrl?: string }>;
  fanCaptureEnabled: boolean;
  fanCaptureLabel: string;
  fields: LinkInBioFormField[];
};

export type LinkInBioTemplateFieldsHandle = {
  getPayload: () => LinkInBioTemplatePayload;
  isValid: () => boolean;
};

type ImagePickerTarget =
  | { kind: "profile" }
  | { kind: "link"; index: number };

const emptySocial = (): SocialRow => ({
  platform: "instagram",
  url: "",
});

const emptyLink = (): LinkRow => ({
  label: "",
  url: "",
  thumbnailUrl: "",
});

function contextToState({ context }: { context?: LinkInBioTemplateContext }) {
  return {
    profilePicUrl: context?.profilePicUrl ?? "",
    socials: (context?.socials ?? []).map((social) => ({
      platform: social.platform || "instagram",
      url: social.url || "",
    })),
    links: (context?.links ?? []).map((link) => ({
      label: link.label || "",
      url: link.url || "",
      thumbnailUrl: link.thumbnailUrl || "",
    })),
    fanCaptureEnabled: context?.fanCaptureEnabled !== false,
    fanCaptureLabel: context?.fanCaptureLabel || DEFAULT_FAN_CAPTURE_LABEL,
    fieldSelections: fieldSelectionsFromFields({ fields: context?.fields }),
  };
}

function normalizeLinkInBioState({
  profilePicUrl,
  socials,
  links,
  fanCaptureEnabled,
  fanCaptureLabel,
  fieldSelections,
}: {
  profilePicUrl: string;
  socials: SocialRow[];
  links: LinkRow[];
  fanCaptureEnabled: boolean;
  fanCaptureLabel: string;
  fieldSelections: LinkInBioFormFieldSelections;
}) {
  return {
    profilePicUrl: profilePicUrl.trim(),
    socials: socials
      .filter((social) => social.platform && social.url.trim())
      .map((social) => ({
        platform: social.platform,
        url: social.url.trim(),
      })),
    links: links
      .filter((link) => link.url.trim())
      .map((link) => ({
        label: link.label.trim(),
        url: link.url.trim(),
        thumbnailUrl: link.thumbnailUrl.trim(),
      })),
    fanCaptureEnabled,
    fanCaptureLabel: fanCaptureLabel.trim(),
    fields: fieldsFromSelections({ selections: fieldSelections }),
  };
}

export const LinkInBioTemplateFields = forwardRef<
  LinkInBioTemplateFieldsHandle,
  {
    context?: LinkInBioTemplateContext;
    disabled?: boolean;
    onDirtyChange?: (dirty: boolean) => void;
  }
>(function LinkInBioTemplateFields({ context, disabled, onDirtyChange }, ref) {
  const initial = contextToState({ context });
  const [profilePicUrl, setProfilePicUrl] = useState(initial.profilePicUrl);
  const [socials, setSocials] = useState<SocialRow[]>(initial.socials);
  const [links, setLinks] = useState<LinkRow[]>(initial.links);
  const [fanCaptureEnabled, setFanCaptureEnabled] = useState(
    initial.fanCaptureEnabled
  );
  const [fanCaptureLabel, setFanCaptureLabel] = useState(initial.fanCaptureLabel);
  const [fieldSelections, setFieldSelections] = useState(initial.fieldSelections);
  const [imagePicker, setImagePicker] = useState<ImagePickerTarget | null>(null);

  useEffect(() => {
    const next = contextToState({ context });
    setProfilePicUrl(next.profilePicUrl);
    setSocials(next.socials);
    setLinks(next.links);
    setFanCaptureEnabled(next.fanCaptureEnabled);
    setFanCaptureLabel(next.fanCaptureLabel);
    setFieldSelections(next.fieldSelections);
  }, [context]);

  useEffect(() => {
    const current = normalizeLinkInBioState({
      profilePicUrl,
      socials,
      links,
      fanCaptureEnabled,
      fanCaptureLabel,
      fieldSelections,
    });
    const saved = normalizeLinkInBioState(contextToState({ context }));
    onDirtyChange?.(JSON.stringify(current) !== JSON.stringify(saved));
  }, [
    profilePicUrl,
    socials,
    links,
    fanCaptureEnabled,
    fanCaptureLabel,
    fieldSelections,
    context,
    onDirtyChange,
  ]);

  useImperativeHandle(ref, () => ({
    getPayload: () => ({
      profilePicUrl: profilePicUrl.trim() || undefined,
      socials: socials
        .filter((social) => social.platform && social.url.trim())
        .map((social) => ({
          platform: social.platform,
          url: social.url.trim(),
        })),
      links: links
        .filter((link) => link.url.trim())
        .map((link) => ({
          label: link.label.trim(),
          url: link.url.trim(),
          thumbnailUrl: link.thumbnailUrl.trim() || undefined,
        })),
      fanCaptureEnabled,
      fanCaptureLabel: fanCaptureLabel.trim(),
      fields: fieldsFromSelections({ selections: fieldSelections }),
    }),
    isValid: () => !fanCaptureEnabled || Boolean(fanCaptureLabel.trim()),
  }));

  const handleImageSelect = ({
    url,
  }: {
    fileId: string;
    url: string;
    video?: boolean;
  }) => {
    if (!imagePicker) return;
    if (imagePicker.kind === "profile") {
      setProfilePicUrl(url);
    } else {
      setLinks((prev) =>
        prev.map((link, index) =>
          index === imagePicker.index ? { ...link, thumbnailUrl: url } : link
        )
      );
    }
    setImagePicker(null);
  };

  return (
    <>
      <div className="space-y-6">
        <div className="space-y-2">
          <Label>Profile Picture</Label>
          <div className="flex items-center gap-3">
            {profilePicUrl ? (
              <img
                src={profilePicUrl}
                alt=""
                className="h-16 w-16 rounded-full object-cover"
              />
            ) : (
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-muted text-muted-foreground">
                <ImageIcon className="h-6 w-6" />
              </div>
            )}
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setImagePicker({ kind: "profile" })}
                disabled={disabled}
              >
                {profilePicUrl ? "Change" : "Select image"}
              </Button>
              {profilePicUrl ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setProfilePicUrl("")}
                  disabled={disabled}
                >
                  Remove
                </Button>
              ) : null}
            </div>
          </div>
        </div>

        <div className="space-y-2">
          <Label>Socials</Label>
          <div className="space-y-3">
            {socials.map((social, index) => (
              <div key={index} className="flex items-start gap-2">
                <Select
                  value={social.platform}
                  onValueChange={(value) =>
                    setSocials((prev) =>
                      prev.map((row, rowIndex) =>
                        rowIndex === index ? { ...row, platform: value } : row
                      )
                    )
                  }
                  disabled={disabled}
                >
                  <SelectTrigger className="w-[160px] shrink-0">
                    <SelectValue placeholder="Platform" />
                  </SelectTrigger>
                  <SelectContent>
                    {LINK_IN_BIO_SOCIAL_PLATFORMS.map((platform) => (
                      <SelectItem key={platform.value} value={platform.value}>
                        {platform.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  value={social.url}
                  onChange={(event) =>
                    setSocials((prev) =>
                      prev.map((row, rowIndex) =>
                        rowIndex === index
                          ? { ...row, url: event.target.value }
                          : row
                      )
                    )
                  }
                  placeholder="https://"
                  disabled={disabled}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="shrink-0 text-muted-foreground hover:text-destructive"
                  onClick={() =>
                    setSocials((prev) =>
                      prev.filter((_, rowIndex) => rowIndex !== index)
                    )
                  }
                  disabled={disabled}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setSocials((prev) => [...prev, emptySocial()])}
              disabled={disabled}
            >
              <Plus className="h-4 w-4" />
              Add social
            </Button>
          </div>
        </div>

        <div className="space-y-2">
          <Label>Links</Label>
          <div className="space-y-3">
            {links.map((link, index) => (
              <div key={index} className="space-y-2 rounded-lg border p-3">
                <div className="flex items-center gap-3">
                  {link.thumbnailUrl ? (
                    <img
                      src={link.thumbnailUrl}
                      alt=""
                      className="h-12 w-12 shrink-0 object-cover"
                    />
                  ) : (
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center bg-muted text-muted-foreground">
                      <ImageIcon className="h-5 w-5" />
                    </div>
                  )}
                  <div className="flex flex-wrap gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setImagePicker({ kind: "link", index })}
                      disabled={disabled}
                    >
                      {link.thumbnailUrl ? "Change thumbnail" : "Add thumbnail"}
                    </Button>
                    {link.thumbnailUrl ? (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() =>
                          setLinks((prev) =>
                            prev.map((row, rowIndex) =>
                              rowIndex === index ? { ...row, thumbnailUrl: "" } : row
                            )
                          )
                        }
                        disabled={disabled}
                      >
                        Remove
                      </Button>
                    ) : null}
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="ml-auto shrink-0 text-muted-foreground hover:text-destructive"
                    onClick={() =>
                      setLinks((prev) =>
                        prev.filter((_, rowIndex) => rowIndex !== index)
                      )
                    }
                    disabled={disabled}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
                <Input
                  value={link.label}
                  onChange={(event) =>
                    setLinks((prev) =>
                      prev.map((row, rowIndex) =>
                        rowIndex === index
                          ? { ...row, label: event.target.value }
                          : row
                      )
                    )
                  }
                  placeholder="Label"
                  disabled={disabled}
                />
                <Input
                  value={link.url}
                  onChange={(event) =>
                    setLinks((prev) =>
                      prev.map((row, rowIndex) =>
                        rowIndex === index ? { ...row, url: event.target.value } : row
                      )
                    )
                  }
                  placeholder="https://"
                  disabled={disabled}
                />
              </div>
            ))}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setLinks((prev) => [...prev, emptyLink()])}
              disabled={disabled}
            >
              <Plus className="h-4 w-4" />
              Add link
            </Button>
          </div>
        </div>

        <div className="space-y-3 rounded-lg border p-4">
          <div className="flex items-center justify-between gap-4">
            <div className="space-y-1">
              <Label htmlFor="fan-capture-enabled">Fan Capture</Label>
              <p className="text-sm text-muted-foreground">
                Collect contact information from fans.
              </p>
            </div>
            <Switch
              id="fan-capture-enabled"
              checked={fanCaptureEnabled}
              onCheckedChange={setFanCaptureEnabled}
              disabled={disabled}
            />
          </div>
          {fanCaptureEnabled ? (
            <div className="space-y-4 border-t pt-3">
              <div className="space-y-2">
                <Label htmlFor="fan-capture-label">Button label</Label>
                <Input
                  id="fan-capture-label"
                  value={fanCaptureLabel}
                  onChange={(event) => setFanCaptureLabel(event.target.value)}
                  placeholder={DEFAULT_FAN_CAPTURE_LABEL}
                  disabled={disabled}
                />
                <p className="text-sm text-muted-foreground">
                  Shown as a button like the links above. Clicking it opens the
                  fan capture form.
                </p>
              </div>
              <div className="space-y-2">
                <Label>Requested fields</Label>
                <p className="text-sm text-muted-foreground">
                  Choose which details to collect and whether each is required.
                </p>
                <div className="space-y-2">
                  {LINK_IN_BIO_FORM_FIELD_OPTIONS.map((option) => {
                    const selection = fieldSelections[option.field];
                    return (
                      <div
                        key={option.field}
                        className="flex items-center justify-between gap-3 rounded-md border px-3 py-2"
                      >
                        <label
                          htmlFor={`link-in-bio-field-${option.field}`}
                          className="flex items-center gap-3 text-sm"
                        >
                          <Checkbox
                            id={`link-in-bio-field-${option.field}`}
                            checked={selection.requested}
                            disabled={disabled}
                            onCheckedChange={(checked) =>
                              setFieldSelections((prev) => ({
                                ...prev,
                                [option.field]: {
                                  ...prev[option.field],
                                  requested: checked === true,
                                },
                              }))
                            }
                          />
                          <span>{option.label}</span>
                        </label>
                        <label
                          htmlFor={`link-in-bio-required-${option.field}`}
                          className="flex items-center gap-2 text-sm text-muted-foreground"
                        >
                          <Checkbox
                            id={`link-in-bio-required-${option.field}`}
                            checked={selection.required}
                            disabled={disabled || !selection.requested}
                            onCheckedChange={(checked) =>
                              setFieldSelections((prev) => ({
                                ...prev,
                                [option.field]: {
                                  ...prev[option.field],
                                  required: checked === true,
                                },
                              }))
                            }
                          />
                          Required
                        </label>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>

      <ImageSelectDialog
        open={imagePicker !== null}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) setImagePicker(null);
        }}
        onSelect={handleImageSelect}
        title={imagePicker?.kind === "link" ? "Select Thumbnail" : "Select Image"}
        description="Choose an image from your uploaded files or upload a new one"
      />
    </>
  );
});
